import { useMemo, useState } from 'react';

import type { EChartsOption } from 'echarts';
import ReactECharts from 'echarts-for-react';

import { Loader } from '@/components/Loader';
import { graphConfig } from '@/configs/Graphs';
import { LegendToggle } from '@/features/forecasts/components/forecast-details/LegendToggle';
import {
  BFG_COLOR,
  INCUMBENT_COLOR,
  toModelLeadtimeSpans,
  type FctModelConfig,
} from '@/features/forecasts/config/fctRuns';
import {
  FORECAST_PERCENTILE_COLORS,
  FORECAST_PERCENTILE_KEYS,
  FORECAST_PERCENTILE_LABELS,
} from '@/features/forecasts/config/percentiles';
import type { EventBehaviorTimeseriesPoint, ModelOutputRecord } from '@/features/forecasts/forecasts.api.types';
import { selectedLeadtimeSpans, type FctForecast } from '@/features/forecasts/hooks/useFctForecasts';
import { useForecastQueryState } from '@/features/forecasts/hooks/useForecastQueryState';
import { useOrderData } from '@/features/forecasts/hooks/useOrderData';
import { useFctForecastsContext } from '@/features/forecasts/providers/FctForecastsProvider';
import type { PercentileKey, VisibleSeries } from '@/features/forecasts/types';
import {
  FCT_DATA_ZOOM,
  fctActualsLollipop,
  fctCategoryAxis,
  fctForecastBoundaryMarkLine,
  fctValueAxis,
} from '@/features/forecasts/utils/fctChartLayout';
import { formatIsoDateMd } from '@/utils/date';

type Props = {
  chartGroup?: string;
  config: FctModelConfig;
  model: 'bfg' | 'incumbent';
  title?: string;
  // Optional content rendered in the header, to the right of the legend (e.g. the WAPE callout).
  headerExtra?: React.ReactNode;
};

// BFG defaults: P50 visible, mean off (P50 is the headline forecast metric).
const BFG_DEFAULT_VISIBLE: VisibleSeries = {
  mean: false,
  p10: false,
  p20: false,
  p30: false,
  p50: true,
  p70: false,
  p80: false,
  p90: false,
};
// Incumbent only offers a mean — no percentiles are exposed.
const INCUMBENT_DEFAULT_VISIBLE: VisibleSeries = {
  mean: true,
  p10: false,
  p20: false,
  p30: false,
  p50: false,
  p70: false,
  p80: false,
  p90: false,
};

export type Point = { iso: string; mean: number | null } & Record<PercentileKey, number | null>;

// Reduce each fct's forecast to a single dated point — the record matching the
// model's requested [lead_time, span] (already in the model's own grain) —
// positioned by the response's own timestamp. The API decides where each point
// lands; we plot the value exactly as returned (no target-based scaling).
export function reduceToPoints(series: FctForecast[], plotSpan: [number, number]): Point[] {
  const [plotLeadTime, plotSpanDays] = plotSpan;
  return series
    .map(({ forecast }) => {
      // Match on the requested grain. ETS omits lead_time in its response, so a
      // missing lead_time counts as a match; a single-record response is taken
      // as-is (nothing else to disambiguate against).
      const matches = (r: ModelOutputRecord) =>
        (r.lead_time ?? plotLeadTime) === plotLeadTime && r.span === plotSpanDays;
      const record = forecast.find(matches) ?? (forecast.length === 1 ? forecast[0] : undefined);
      if (!record?.timestamp) return null;
      const iso = record.timestamp.slice(0, 10);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
      const o = record.target_output;
      return {
        iso,
        mean: o.mean ?? null,
        p10: o.quantile_10 ?? null,
        p20: o.quantile_20 ?? null,
        p30: o.quantile_30 ?? null,
        p50: o.quantile_50 ?? null,
        p70: o.quantile_70 ?? null,
        p80: o.quantile_80 ?? null,
        p90: o.quantile_90 ?? null,
      } satisfies Point;
    })
    .filter((p): p is Point => p != null)
    .sort((a, b) => a.iso.localeCompare(b.iso));
}

function buildChartOption(
  points: Point[],
  actuals: EventBehaviorTimeseriesPoint[],
  visibleSeries: VisibleSeries,
  actualsVisible: boolean,
  labels: { mean: string; actuals: string },
  meanColor: string,
  showPercentiles: boolean,
): EChartsOption {
  const { grid, colors } = graphConfig;

  // Actuals run only up to the last forecast point date.
  const lastForecastIso = points.length ? points[points.length - 1].iso : '';
  const visibleActuals = actuals.filter((a) => a.date && (!lastForecastIso || a.date <= lastForecastIso));

  // Unified, sorted x-axis across actuals and forecast points.
  const allDates = [...new Set([...visibleActuals.map((a) => a.date), ...points.map((p) => p.iso)])].sort();
  const axisLabels = allDates.map((d) => formatIsoDateMd(d));

  const actualsByDate = new Map(visibleActuals.map((a) => [a.date, a.quantity ?? 0]));
  const pointByDate = new Map(points.map((p) => [p.iso, p]));

  // Boundary sits on the first forecast point — everything left of it is history.
  const boundaryLabel = points.length ? formatIsoDateMd(points[0].iso) : null;

  const lineSeries = (name: string, key: keyof Point, color: string, visible: boolean, width: number) => ({
    name,
    type: 'line' as const,
    // Hidden series emit no data so they don't stretch the y-axis extent. (Just
    // setting opacity to 0 keeps the values in axis scaling, which let hidden
    // high percentiles squash the visible lines.)
    data: visible ? allDates.map((d) => (pointByDate.get(d)?.[key] as number | null | undefined) ?? null) : [],
    connectNulls: true,
    lineStyle: { color, width },
    itemStyle: { color },
    symbol: 'circle' as const,
    symbolSize: 5,
    showSymbol: true,
    emphasis: { focus: 'series' as const },
  });

  return {
    tooltip: { trigger: 'axis' },
    grid,
    xAxis: fctCategoryAxis(axisLabels),
    yAxis: fctValueAxis(),
    dataZoom: FCT_DATA_ZOOM,
    series: [
      ...fctActualsLollipop(
        labels.actuals,
        allDates.map((d) => (actualsVisible ? (actualsByDate.get(d) ?? null) : null)),
        colors.actuals,
      ),
      ...(showPercentiles
        ? FORECAST_PERCENTILE_KEYS.map((key) =>
            lineSeries(
              `P${FORECAST_PERCENTILE_LABELS[key]}`,
              key,
              FORECAST_PERCENTILE_COLORS[key],
              visibleSeries[key],
              2,
            ),
          )
        : []),
      lineSeries(labels.mean, 'mean', meanColor, visibleSeries.mean, 3),
      fctForecastBoundaryMarkLine(boundaryLabel),
    ],
  } satisfies EChartsOption;
}

export function FctForecastChart({ chartGroup, config, model, title, headerExtra }: Props) {
  const { state } = useForecastQueryState();
  const { series, isLoading } = useFctForecastsContext()[model];
  const { actuals } = useOrderData();
  const showPercentiles = model === 'bfg';
  const meanColor = model === 'incumbent' ? INCUMBENT_COLOR : BFG_COLOR;
  const [visibleSeries, setVisibleSeries] = useState<VisibleSeries>(
    model === 'incumbent' ? INCUMBENT_DEFAULT_VISIBLE : BFG_DEFAULT_VISIBLE,
  );
  const [actualsVisible, setActualsVisible] = useState(true);

  // The plotted record is the model's request span for the selected LTSP.
  const plotSpan = toModelLeadtimeSpans(config, selectedLeadtimeSpans(state.leadTime, state.span))[0] ?? null;

  const points = useMemo(() => (plotSpan ? reduceToPoints(series, plotSpan) : []), [series, plotSpan]);

  const toggleSeries = (key: keyof VisibleSeries) => setVisibleSeries((prev) => ({ ...prev, [key]: !prev[key] }));

  const meanLabel = 'Mean';
  const actualsLabel = 'Actuals';

  if (isLoading) return <Loader />;
  if (!points.length) {
    return (
      <div className="py-12 text-center text-sm text-gray-400">{`No ${config.label} forecast data for this selection`}</div>
    );
  }

  const option = buildChartOption(
    points,
    actuals,
    visibleSeries,
    actualsVisible,
    { mean: meanLabel, actuals: actualsLabel },
    meanColor,
    showPercentiles,
  );

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-x-6 gap-y-2">
        {title ? <h3 className="text-sm font-semibold text-gray-900">{title}</h3> : <span />}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-600">
          <LegendToggle
            label={actualsLabel}
            color={graphConfig.colors.actuals}
            active={actualsVisible}
            onClick={() => setActualsVisible((v) => !v)}
          />
          <LegendToggle
            label={meanLabel}
            color={meanColor}
            active={visibleSeries.mean}
            onClick={() => toggleSeries('mean')}
          />
          {showPercentiles &&
            FORECAST_PERCENTILE_KEYS.map((key) => (
              <LegendToggle
                key={key}
                label={`P${FORECAST_PERCENTILE_LABELS[key]}`}
                color={FORECAST_PERCENTILE_COLORS[key]}
                active={visibleSeries[key]}
                onClick={() => toggleSeries(key)}
              />
            ))}
          {headerExtra}
        </div>
      </div>
      <ReactECharts
        opts={{ renderer: 'svg' }}
        option={option}
        notMerge
        style={{ height: 360 }}
        onChartReady={(chart) => {
          if (chartGroup) chart.group = chartGroup;
        }}
      />
    </>
  );
}
